import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { QT_DAILY_COCKPIT_URL, qaHref } from './contextLinks';

// Mirrors apps/sales-qa/src/qa.ts RetrievedRecord
type Kind =
  | 'call'
  | 'call_review'
  | 'sms'
  | 'sms_msg'           // INF-65: per-message hits
  | 'form'
  | 'deal'
  | 'project_scope'
  | 'deal_task'
  | 'action_item'
  | 'hubspot_archive'
  | 'contact'
  | 'manual_engagement'
  | 'meeting_transcript'
  | 'gchat';            // SQA-69: Google Chat named-space messages

interface Citation {
  kind: Kind;
  id: string;
  rank: number;
  occurred_at: string | null;
  title: string;
  summary: string;
  fields: Record<string, unknown>;
}

interface QaResponse {
  answer: string;
  citations: Citation[];
  retrieval: {
    total_hits: number;
    per_kind: Partial<Record<Kind, number>>;
    elapsed_ms: number;
  };
  model: string;
}

type ViewState =
  | { kind: 'idle' }
  | { kind: 'asking'; question: string }
  | { kind: 'answered'; question: string; response: QaResponse }
  | { kind: 'error'; question: string; message: string };

type CitationAction =
  | { kind: 'internal'; label: string; to: string; tone?: 'primary' | 'neutral'; hint?: string }
  | { kind: 'external'; label: string; href: string; tone?: 'primary' | 'neutral'; hint?: string };

const KIND_LABEL: Record<Kind, string> = {
  call: 'Call',
  call_review: 'Call review',
  sms: 'SMS thread',
  sms_msg: 'SMS message',
  form: 'Form',
  deal: 'Deal',
  project_scope: 'Project scope',
  deal_task: 'Deal task',
  action_item: 'Action item',
  hubspot_archive: 'HubSpot archive',
  contact: 'Contact',
  manual_engagement: 'Manual engagement',
  meeting_transcript: 'Meeting transcript',
  gchat: 'Chat',
};

const KIND_BG: Record<Kind, string> = {
  call: 'bg-amber-50 ring-amber-200 text-amber-900',
  call_review: 'bg-amber-100 ring-amber-300 text-amber-950',
  sms: 'bg-sky-50 ring-sky-200 text-sky-900',
  sms_msg: 'bg-sky-100 ring-sky-300 text-sky-950',
  form: 'bg-emerald-50 ring-emerald-200 text-emerald-900',
  deal: 'bg-violet-50 ring-violet-200 text-violet-900',
  project_scope: 'bg-green-50 ring-green-200 text-green-900',
  deal_task: 'bg-violet-100 ring-violet-300 text-violet-950',
  action_item: 'bg-rose-50 ring-rose-200 text-rose-900',
  hubspot_archive: 'bg-slate-100 ring-slate-300 text-slate-900',
  contact: 'bg-slate-50 ring-slate-200 text-slate-900',
  manual_engagement: 'bg-teal-50 ring-teal-200 text-teal-900',
  meeting_transcript: 'bg-indigo-50 ring-indigo-200 text-indigo-900',
  gchat: 'bg-lime-50 ring-lime-200 text-lime-900',
};

const EXAMPLES: string[] = [
  'What did we promise the customer on the last call?',
  'Where is the proof of what we committed to?',
  'Has anyone called from Smith Outfitters recently?',
  'Pull all evidence for Whitney Nations.',
  'What did Jason say about the inverter upgrade?',
];

export function QaView(): JSX.Element {
  const [searchParams] = useSearchParams();
  const prefillQuestion = searchParams.get('q') ?? '';
  const [question, setQuestion] = useState('');
  const [state, setState] = useState<ViewState>({ kind: 'idle' });
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const next = prefillQuestion.trim();
    if (next.length === 0) return;
    setQuestion(next);
    setState({ kind: 'idle' });
    inputRef.current?.focus();
  }, [prefillQuestion]);

  async function submit(q: string): Promise<void> {
    const trimmed = q.trim();
    if (trimmed.length === 0) return;
    setState({ kind: 'asking', question: trimmed });
    try {
      const res = await fetch('/api/qa', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ question: trimmed }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setState({
          kind: 'error',
          question: trimmed,
          message: body.error ?? `Request failed: ${res.status}`,
        });
        return;
      }
      const payload = (await res.json()) as QaResponse;
      setState({ kind: 'answered', question: trimmed, response: payload });
    } catch (err) {
      setState({
        kind: 'error',
        question: trimmed,
        message: (err as Error).message,
      });
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      void submit(question);
    }
  }

  return (
    <div className="space-y-6">
      <section>
        <label htmlFor="qa-question" className="sr-only">Ask a question</label>
        <textarea
          id="qa-question"
          ref={inputRef}
          rows={3}
          placeholder="Ask anything about the evidence — calls, SMS, email, forms, prior deals. Try: What did Jason say about the inverter on the call last week?"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          className="block w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
        />
        <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-slate-400">⌘⏎ to send · single-turn only · cites every record it used</p>
          <button
            type="button"
            disabled={state.kind === 'asking' || question.trim().length === 0}
            onClick={() => void submit(question)}
            className="inline-flex w-full items-center justify-center rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400 sm:w-auto"
          >
            {state.kind === 'asking' ? 'Thinking…' : 'Ask'}
          </button>
        </div>
      </section>

      {state.kind === 'idle' && (
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700">Try an example</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {EXAMPLES.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => {
                  setQuestion(ex);
                  void submit(ex);
                }}
                className="rounded-full bg-slate-100 px-3 py-1.5 text-left text-xs text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-200 hover:text-slate-900"
              >
                {ex}
              </button>
            ))}
          </div>
        </section>
      )}

      {state.kind === 'asking' && (
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
          <p className="text-sm text-slate-600">
            <span className="inline-block animate-pulse">Searching calls, SMS, deals, forms, HubSpot archive…</span>
          </p>
          <p className="mt-2 text-xs text-slate-400">Question: {state.question}</p>
        </section>
      )}

      {state.kind === 'error' && (
        <section className="rounded-xl bg-rose-50 ring-1 ring-rose-200 p-5">
          <p className="text-sm font-medium text-rose-900">Something broke</p>
          <p className="mt-1 text-sm text-rose-800">{state.message}</p>
          <p className="mt-2 text-xs text-rose-700">Question: {state.question}</p>
        </section>
      )}

      {state.kind === 'answered' && <AnswerView state={state} />}
    </div>
  );
}

function AnswerView({ state }: { state: { kind: 'answered'; question: string; response: QaResponse } }): JSX.Element {
  const { question, response } = state;
  const { answer, citations, retrieval, model } = response;

  return (
    <>
      <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5">
        <p className="text-xs text-slate-400">
          You asked: <span className="text-slate-600">“{question}”</span>
        </p>
        <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-900">
          {renderAnswerWithNumberedRefs(answer, citations)}
        </div>
      </section>

      {citations.length === 0 ? (
        <p className="text-sm text-slate-500">No records matched — try adding a name, a date, or rephrasing.</p>
      ) : (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
            Sources
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-500">{citations.length}</span>
          </h2>
          <ul className="space-y-2">
            {citations.map((c, i) => (
              <li key={c.id}>
                <CitationCard citation={c} index={i + 1} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="text-xs text-slate-400">
        <summary className="cursor-pointer select-none">Search details</summary>
        <p className="mt-1 tabular-nums">
          {retrieval.total_hits} record{retrieval.total_hits === 1 ? '' : 's'} scanned · retrieval {retrieval.elapsed_ms}ms · model {model}
        </p>
      </details>
    </>
  );
}

function renderAnswerWithNumberedRefs(answer: string, citations: Citation[]): React.ReactNode {
  // Map each cited record id → its 1-based position in the Sources list, so the
  // raw [kind#id] tokens render as small numbered refs instead of user-facing
  // jargon. Tapping a ref opens + scrolls to that source.
  const idToNum = new Map<string, number>();
  citations.forEach((c, i) => {
    if (!idToNum.has(c.id)) idToNum.set(c.id, i + 1);
  });
  const out: React.ReactNode[] = [];
  const re = /\[([a-z_]+#[^\]\s]+)\]/g;
  let lastIdx = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(answer)) !== null) {
    out.push(answer.slice(lastIdx, m.index));
    const id = m[1];
    const num = idToNum.get(id);
    if (num !== undefined) {
      out.push(
        <sup key={`${id}-${m.index}`} className="mx-0.5 leading-none">
          <a
            href={`#cite-${cssId(id)}`}
            onClick={() => openCite(id)}
            className="rounded bg-slate-100 px-1 py-0.5 text-[10px] font-semibold text-slate-600 no-underline hover:bg-slate-800 hover:text-white"
          >
            {num}
          </a>
        </sup>,
      );
    }
    // Cited-but-not-retrieved tokens are dropped — never surface raw kind#id.
    lastIdx = m.index + m[0].length;
  }
  out.push(answer.slice(lastIdx));
  return out;
}

// Inline ref tapped → open the (collapsed) source <details> and scroll to it.
function openCite(id: string): void {
  const el = document.getElementById(`cite-${cssId(id)}`);
  if (el instanceof HTMLDetailsElement) {
    el.open = true;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
}

// Short, human date — raw occurred_at can be a long ISO string.
function fmtWhen(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? s
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: '2-digit' });
}

function cssId(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]/g, '_');
}

function fieldString(fields: Record<string, unknown>, key: string): string | null {
  const value = fields[key];
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function citationContactId(citation: Citation): string | null {
  const direct = fieldString(citation.fields, 'contact_id');
  if (direct) return direct;
  if (citation.kind === 'contact' && citation.id.startsWith('contact#')) {
    return citation.id.slice('contact#'.length);
  }
  return null;
}

function safeExternalUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.trim().length === 0) return null;
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
}

function citationQuestion(citation: Citation): string {
  const label = KIND_LABEL[citation.kind].toLowerCase();
  const title = citation.title || citation.id;
  return `What else do we know from this ${label} source: "${title}"? Summarize the evidence, contradictions, and safest next step.`;
}

function citationActions(citation: Citation): CitationAction[] {
  const actions: CitationAction[] = [];
  const contactId = citationContactId(citation);

  if (contactId) {
    const encodedContactId = encodeURIComponent(contactId);
    actions.push({
      kind: 'internal',
      label: 'Customer evidence',
      to: `/contacts/${encodedContactId}`,
      tone: 'primary',
    });
    actions.push({
      kind: 'internal',
      label: 'Preflight',
      to: `/contacts/${encodedContactId}/preflight`,
    });
  }

  actions.push({
    kind: 'internal',
    label: 'Ask about source',
    to: qaHref(citationQuestion(citation)),
  });

  if (citation.kind === 'deal' || citation.kind === 'deal_task') {
    actions.push({
      kind: 'external',
      label: 'Open in QT',
      href: QT_DAILY_COCKPIT_URL,
      hint: 'Leaving Sales-QA evidence. QT owns the next action — reply, scope, quote, close the loop.',
    });
  }

  if (citation.kind === 'form') {
    const sourcePage = safeExternalUrl(citation.fields.page_url);
    if (sourcePage) {
      actions.push({
        kind: 'external',
        label: 'Source page',
        href: sourcePage,
      });
    }
  }

  return actions;
}

function citationActionClass(tone: CitationAction['tone'] = 'neutral'): string {
  return tone === 'primary'
    ? 'inline-flex items-center rounded-md bg-slate-900 px-2.5 py-1.5 text-xs font-medium text-white transition hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400'
    : 'inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400';
}

function CitationCard({ citation, index }: { citation: Citation; index: number }): JSX.Element {
  const chip = KIND_BG[citation.kind];
  const actions = citationActions(citation);
  // Collapsed by default (progressive disclosure) — summary text + raw fields
  // only show on tap. The id stays on the <details> so a ref can open + scroll.
  return (
    <details
      id={`cite-${cssId(citation.id)}`}
      className="group rounded-xl bg-white ring-1 ring-slate-200 open:ring-slate-300"
    >
      <summary className="flex list-none cursor-pointer select-none items-center gap-2.5 rounded-xl p-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 [&::-webkit-details-marker]:hidden">
        <span className="grid h-5 w-5 flex-none place-items-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
          {index}
        </span>
        <span className={`flex-none rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ring-1 ${chip}`}>
          {KIND_LABEL[citation.kind]}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800 group-open:whitespace-normal">
          {citation.title || '(untitled record)'}
        </span>
        {citation.occurred_at ? (
          <span className="flex-none text-[11px] tabular-nums text-slate-400">{fmtWhen(citation.occurred_at)}</span>
        ) : null}
        <span className="flex-none text-[10px] text-slate-400 transition-transform group-open:rotate-180">▼</span>
      </summary>
      <div className="space-y-2 px-3 pb-3 pl-[2.6rem]">
        {citation.summary ? (
          <pre className="whitespace-pre-wrap font-sans text-xs leading-relaxed text-slate-600">{citation.summary}</pre>
        ) : (
          <p className="text-xs italic text-slate-400">No preview text for this record.</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {actions.map((action) => (
            action.kind === 'internal' ? (
              <Link
                key={`${action.label}-${action.to}`}
                to={action.to}
                title={action.hint}
                className={citationActionClass(action.tone)}
              >
                {action.label}
              </Link>
            ) : (
              <a
                key={`${action.label}-${action.href}`}
                href={action.href}
                target="_blank"
                rel="noreferrer"
                title={action.hint}
                className={citationActionClass(action.tone)}
              >
                {action.label}
              </a>
            )
          ))}
        </div>
        <details className="text-xs text-slate-500">
          <summary className="cursor-pointer select-none">Full record fields</summary>
          <pre className="mt-2 whitespace-pre-wrap rounded-md bg-slate-50 p-2 font-mono text-[11px] leading-relaxed">
            {JSON.stringify(citation.fields, null, 2)}
          </pre>
        </details>
      </div>
    </details>
  );
}
